import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  ModalContent,
  SearchInput,
  Button,
  Flex,
  Text,
} from "@appsmith/ads";

import { filterCommands } from "./filterCommands";
import { groupCommands } from "./groupCommands";
import { isCommandPaletteShortcut } from "./isCommandPaletteShortcut";
import { handlePaletteKeyDown } from "./handlePaletteKeyDown";
import { showCommandRunToast } from "./showCommandRunToast";
import { useAnvilCommands } from "./useAnvilCommands";
import { prefersReducedMotion, transition } from "../motion/anvilMotion";
import { createMessage } from "ee/constants/messages";
import { COMMAND_PALETTE_PLACEHOLDER } from "../../common/messages";
import type { AnvilCommand } from "./types";

/**
 * Anvil command palette (Cmd/Ctrl+Shift+P).
 *
 * Mounted ONLY inside the Anvil editor canvas (AnvilEditorCanvas), so its
 * keydown listener exists exclusively in the Anvil editor — non-Anvil editors
 * are untouched and the existing Cmd+K omnibar is unaffected.
 */
export function AnvilCommandPalette() {
  const commands = useAnvilCommands();
  const [isOpen, setIsOpen] = useState(false);
  const [entered, setEntered] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const results = filterCommands(commands, query);

  // Reset the highlight to the top whenever the result set changes (a new
  // query re-ranks the list, so a stale index could point at the wrong row).
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Subtle open animation (fade + slide). Anvil-editor-only.
  useEffect(() => {
    if (!isOpen) {
      setEntered(false);

      return;
    }

    const frame = requestAnimationFrame(() => setEntered(true));

    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isCommandPaletteShortcut(event)) {
        event.preventDefault();
        setIsOpen((open) => !open);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
  }, []);

  const runCommand = useCallback(
    (command: AnvilCommand) => {
      command.run();
      showCommandRunToast(command);
      close();
    },
    [close],
  );

  return (
    <Modal onOpenChange={(open) => !open && close()} open={isOpen}>
      <ModalContent data-testid="t--anvil-command-palette">
        {/* onKeyDown is on the wrapper so it catches keydown bubbling from the
            focused SearchInput (a sibling of the results list) — this is what
            lets ArrowUp/ArrowDown move the highlight and Enter run the active
            result while the user is still typing in the search box. */}
        <Flex
          flexDirection="column"
          gap="spaces-3"
          onKeyDown={(event) => {
            if (
              handlePaletteKeyDown(event.key, {
                results,
                activeIndex,
                setActiveIndex,
                runCommand,
                close,
              })
            ) {
              event.preventDefault();
            }
          }}
          style={
            prefersReducedMotion()
              ? undefined
              : {
                  opacity: entered ? 1 : 0,
                  transform: entered ? "translateY(0)" : "translateY(-4px)",
                  transition: transition(["opacity", "transform"]),
                }
          }
        >
          <SearchInput
            autoFocus
            onChange={setQuery}
            placeholder={createMessage(COMMAND_PALETTE_PLACEHOLDER)}
            value={query}
          />
          <Flex flexDirection="column" gap="spaces-1">
            {results.length === 0 ? (
              <Text kind="body-m">No commands found</Text>
            ) : (
              groupCommands(results).map((group) => (
                <React.Fragment key={group.items[0].index}>
                  {group.group ? (
                    <Text color="var(--ads-v2-color-fg-muted)" kind="body-s">
                      {group.group}
                    </Text>
                  ) : null}
                  {group.items.map(({ command, index }) => (
                    <Button
                      data-testid="t--anvil-command-palette-result"
                      key={command.id}
                      kind={index === activeIndex ? "secondary" : "tertiary"}
                      onClick={() => runCommand(command)}
                      onMouseEnter={() => setActiveIndex(index)}
                      size="md"
                    >
                      {command.label}
                    </Button>
                  ))}
                </React.Fragment>
              ))
            )}
          </Flex>
        </Flex>
      </ModalContent>
    </Modal>
  );
}
